import { List, Map } from "immutable"
import * as React from "react"
import { Action, mk_widget, Widget } from "widgets-for-react"
import { getActivities, getDatesForPeriod, getGooglePlacesOpeningHours, getPage, Region } from "../../custom_api/api"
import { emptyOpt, makeOpt } from "../../custom_utils/option"
import { AddressItem, ParsedParagraphOpeningHours } from "../../../Shared/drupal_models/types"
import { Block as BuildingBlock, BlockRenderer } from "../components/block_renderer"
import { contactDetails, membership } from "../components/contact"
import { pushExpand, pushLocationEvent, pushVenueMembership } from "../components/datalayer"
import { FeaturedContentBlock, FeaturedContentBlockProps } from "../components/featured_content_block"
import { getDrupalCurrentOpeningHours, getGoogleCurrentOpeningHours, Jumbo } from "../components/jumbo"
import { LinkButton } from "../components/library/button"
import { Container } from "../components/library/container"
import { ExpandItem, ExpandProps } from "../components/library/expandItem"
import { Link } from "../components/library/link"
import { HomePageProps } from "./home_page"
import * as DrupalModel from '../../custom_models/drupal'
import { TippedActivitySummary } from "../components/tipped_activity_summary"
import { ActivitySummaryWithFirstDateProps, firstDate } from "../components/tipped_activities_block"
import { ApiResult, isLoaded } from "../../../Shared/api/apiTypes"

export interface LocationDetailPageDrupalProps {
  readonly kind: "locationDetail"
  readonly locationId: number
  readonly pageTitle: string
  readonly headerTitle: string
  readonly subtitle: string,
  readonly description: string
  readonly imageUrl: string
  readonly imageAlt: string
  readonly currentTitle: string
  readonly currentContents: string
  readonly aboutTitle: string
  readonly aboutContents: string
  readonly openingHours: List<ParsedParagraphOpeningHours>
  readonly googlePlacesId?: string,
  readonly contactCustomerSupport: string
  readonly resetPeriod?: () => void
  readonly blocks: List<BuildingBlock>
  readonly email: string,
  readonly phoneNumber: string,
  readonly address: AddressItem,
  readonly selectedLocationId: string
  readonly branchLocations: DrupalModel.Location[]
  readonly selectLocation: (selectedId: string) => void
  readonly pageLanguage: string
}

type LocationDetailPageProps = LocationDetailPageDrupalProps & {
  readonly regions: ApiResult<Map<string, Region>>
}

export interface SpecialDay {
  Date: Date
  ExeptionalHours: boolean
}
export interface Period {
  close: {
    day: number, 
    hour: number, 
    minute: number,
    readableTime: string
  },
  open: {
    day: number,
    hour: number, 
    minute: number, 
    readableTime: string
  }
}

export enum SecondaryHoursType {
  SECONDARY_HOURS_TYPE_UNSPECIFIED = 0,
  DRIVE_THROUGH = 1,
  HAPPY_HOUR = 2,
  DELIVERY = 3,
  TAKEOUT = 4,
  KITCHEN = 5,
  BREAKFAST = 6,
  LUNCH = 7,
  DINNER = 8,
  BRUNCH = 9,
  PICKUP = 10,
  ACCESS = 11,
  SENIOR_HOURS = 12,
  ONLINE_SERVICE_HOURS = 13,
}

export enum DrupalDaysOfWeek {
  Zondag = 0, 
  Maandag = 1, 
  Dinsdag = 2, 
  Woensdag = 3, 
  Donderdag = 4, 
  Vrijdag = 5, 
  Zaterdag = 6
}
export interface GooglePlacesOpeningHours {
  specialDays: SpecialDay[]
  periods: Period[],
  type: SecondaryHoursType,
  weekdayText: string[],
}
export interface LocationDetailPageState {
  activitySummaries: ApiResult<List<ActivitySummaryWithFirstDateProps>>,
  homePageData: ApiResult<HomePageProps>
  openingTimesOpen: boolean
  facilitiesOpen: boolean
  parkingOpen: boolean
  contactOpen: boolean
  membershipOpen: boolean
  googlePlacesOpeningHours: ApiResult<GooglePlacesOpeningHours>
}

const locationDetailPageStateZero: LocationDetailPageState = {
  activitySummaries: "loading",
  homePageData: "loading",
  openingTimesOpen: false,
  facilitiesOpen: false,
  parkingOpen: false,
  contactOpen: false,
  membershipOpen: false,
  googlePlacesOpeningHours: "loading"
}

export class LocationDetailPage extends React.Component<LocationDetailPageProps, LocationDetailPageState> {
  constructor(props: LocationDetailPageProps) {
    super(props)
    this.state = locationDetailPageStateZero
  }

  componentDidMount() {
    const p = this.props
    const s = this.state

    const location = p.headerTitle
    if (isLoaded(p.regions)) {
      const region = p.regions.find(r => r.locations.includes(this.props.locationId))
      if (region) {
        pushLocationEvent({ location, region: region.name })
      } else {
        pushLocationEvent({ location })
      }
    }

    if (s.activitySummaries == "loading") {
      getActivities({
        cluster: emptyOpt(),
        activity: makeOpt(q => q.Where(a =>
          a.HideInOverview.Equals(false)
        )),
        edition: emptyOpt(),
        location: makeOpt(q => p.locationId ? q.Where(l => l.Id.Equals(p.locationId)) : q),
        region: emptyOpt(),
        event: makeOpt(q => q.Where(e => {
          let period = getDatesForPeriod('any')

          return e.Date.IsAfter(period.start.toDate())
            .And(e.Date.IsBefore(period.end.toDate()))
        }))
      },
      firstDate
      ).then(x => {
        // the use of `firstDate` above guarantees that we can do the following cast
        this.setState((s, _) => ({ ...s, activitySummaries: x as ApiResult<List<ActivitySummaryWithFirstDateProps>> }))
      })
    }
    if (s.homePageData == "loading") {
      getPage('/home', "").then(x => this.setState(s => ({ ...s, homePageData: x as HomePageProps })))
    }
    // NOTE: If the Google Places ID is not filled in, we use the Drupal Opening Hours data as a fallback
    if (p.googlePlacesId != undefined && s.googlePlacesOpeningHours === "loading") {
      getGooglePlacesOpeningHours(p.googlePlacesId).then(x => this.setState(s => ({ ...s, googlePlacesOpeningHours: x as GooglePlacesOpeningHours })))
    }

    document.title = this.props.pageTitle
  }

  componentDidUpdate(prevProps: LocationDetailPageProps, prevState: LocationDetailPageState) {
    const prevRegions = prevProps.regions
    const regions = this.props.regions
    const location = this.props.headerTitle

    // If regions fail to load, push location without region and exit
    if (prevRegions != 'error' && regions == 'error') {
      pushLocationEvent({ location })
      return
    }

    // If regions load, try to find the current and push
    if (prevRegions == 'loading' && isLoaded(regions)) {
      const region = regions.find(r => r.locations.includes(this.props.locationId))
      if (region) {
        pushLocationEvent({ location, region: region.name })
      } else {
        pushLocationEvent({ location })
      }
    }
  }

  render() {
    const p = this.props
    const s = this.state

    let regionQuery = ''
    if (isLoaded(p.regions)) {
      const region = p.regions.find(r => r.locations.includes(p.locationId))
      regionQuery = region ? `?waar=${region.slug}` : ''
    }

    const feature = s.homePageData != 'error' && s.homePageData != 'loading'
      ? s.homePageData.blocks.find(b => b.kind == 'featured_content') as FeaturedContentBlockProps
      : null

    return (
      <>
      {s.homePageData != "loading" && s.homePageData != "error" && s.homePageData.showAnnouncement && (
        <div className="announcement" role="alert">
          <div className="container-fluid" dangerouslySetInnerHTML={{ __html: s.homePageData.announcement || ''}} />
        </div>
      )}

      <div className="wide location-page">
        { <Jumbo
          kind={"home-location"}
          headerTitle={p.headerTitle}
          contents={p.description}
          headerImageUrl={p.imageUrl}
          headerImageAlt={p.imageAlt}
          size="large"
          email={p.email}
          phoneNumber={p.phoneNumber ?? ""}
          address={p.address?.address_line1 ?? ""}
          openingHours={p.openingHours}
          googlePlacesOpeningHours={p.googlePlacesId != undefined ? s.googlePlacesOpeningHours : undefined}
          pageTitle={p.pageTitle??""}
          selectedLocationId={p.selectedLocationId}
          branchLocations={p.branchLocations}
          selectLocation={p.selectLocation}
        />}

        {feature ? (
          <Container>
            <hr />
            <FeaturedContentBlock {...feature} />
            <hr />
          </Container>
        ) : <></>}

        {s.activitySummaries == "loading" || s.activitySummaries == "error" || s.activitySummaries.isEmpty() ? null : (
          <div className="container-fluid">
            <section className="block block--tips">
              <div className="block__inner">
                <header className="block__header">
                  <h2 className="block__title title--m">Wat is hier te doen?</h2>
                  <p className="block__intro text--styled">In deze vestiging wordt van alles georganiseerd. Dit zijn de eerstvolgende activiteiten.</p>
                </header>

                <div className="block__content">
                  <ul className="row teasers">
                    {s.activitySummaries.take(6).map((s, i) => (
                      <TippedActivitySummary
                        key={`activity-summary-${i}`}
                        headingLevel="h3"
                        title={s.title}
                        date={s.date}
                        imageAlt={s.imageAlt}
                        imageUrl={s.imageUrl}
                        subtitle={s.subtitle}
                        location={s.location}
                        route={s.route + regionQuery}
                        isTip={s.isTip}
                        row_count={3}
                      />
                    ))}
                  </ul>
                </div>

                <footer className="block__actions">
                  <LinkButton
                    className="grid__button"
                    handle={p.resetPeriod}
                    route={`/activiteiten${regionQuery}`}
                  >
                    Bekijk alle activiteiten
                  </LinkButton>
                  <Link className="grid__button" url="/terugblikken">Bekijk alle terugblikken</Link>
                </footer>
              </div>
            </section>
          </div>
        )}

        <section className="block block--expand">
          <Container>
            <div className="block__content">
            <div className="opening_hours_anchor" id="openingstijden"></div>
              <ul className="block__list expand__list">
                {Expand({
                  innerKey: "openinghours",
                  open: s.openingTimesOpen,
                  title: "Openingstijden",
                  child: (
                    <>
                      <OpeningTimes drupalOpeningHours={p.openingHours} googleOpeningHours={p.googlePlacesId && s.googlePlacesOpeningHours != 'error' && s.googlePlacesOpeningHours != 'loading' ? s.googlePlacesOpeningHours : null}/>
                    </>
                  )
                }).run(action => {
                  let openNew = action(s.openingTimesOpen)
                  if (openNew) { pushExpand('Openingstijden') }
                  this.setState({ ...s, openingTimesOpen: openNew })
                })}
                {Expand({
                  innerKey: "facilities",
                  open: s.facilitiesOpen,
                  title: "Voorzieningen",
                  text: p.aboutContents
                }).run(action => {
                  let openNew = action(s.facilitiesOpen)
                  if (openNew) { pushExpand('Voorzieningen') }
                  this.setState({ ...s, facilitiesOpen: openNew })
                })}
                {Expand({
                  innerKey: "contact",
                  open: s.contactOpen,
                  title: "Contact klantenservice",
                  child: contactDetails(p.contactCustomerSupport)
                }).run(action => {
                  let openNew = action(s.contactOpen)
                  if (openNew) { pushExpand('Contact') }
                  this.setState({ ...s, contactOpen: openNew })
                })}
                {Expand({
                  innerKey: "membership",
                  open: s.membershipOpen,
                  title: "Nog geen lid? Lees dan verder",
                  child: membership()
                }).run(action => {
                  let openNew = action(s.membershipOpen)
                  if (openNew) { pushVenueMembership() }
                  this.setState({ ...s, membershipOpen: openNew })
                })}
              </ul>
            </div>
          </Container>
        </section>

        <BlockRenderer blocks={p.blocks} />
      </div>
      </>
    )
  }
}

const OpeningTimes = (props: {googleOpeningHours: GooglePlacesOpeningHours | null, drupalOpeningHours: List<ParsedParagraphOpeningHours>}) => {
  return (
    <div className="opening-hours__container">
      {[1, 2, 3, 4, 5, 6, 0].
        map(day => {
          const googleH = props.googleOpeningHours != null && getGoogleCurrentOpeningHours(props.googleOpeningHours, day)
          const drupalH = getDrupalCurrentOpeningHours(props.drupalOpeningHours, day)
          return (
          <div key={`opening-hours-entry-${day}`} className="opening-hours__wrapper">
            <div className="opening-hours__info">
              <p className="opening-hours__day">{DrupalDaysOfWeek[day]}</p>
              {<p className="opening-hours__time">{googleH ? googleH : drupalH && (drupalH.closed ? 'gesloten' : drupalH.readableTime)}</p> }
            </div>
            {googleH != 'gesloten' && drupalH && drupalH.additionalInformation != null && <p className="opening-hours__text">{drupalH.additionalInformation}</p>}
          </div>
        )})}
    </div>
  )
}

function Expand(
  props: ExpandProps,
): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone => ExpandItem({ onClick: () => onDone(s => !s), ...props })
  })
}
